import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, map, tap } from 'rxjs';

import { MyWorldHttpService } from '@app/core/services/api/my-world-http.service';
import { AppContextService } from '@app/core/services/app-context.service';
import { LoadingStateEnum } from '@app/core/ui/enums';
import { DeadlineDatesModel } from '@app/features/home/models/deadline.model';
import { DeadlineNameEnum } from '@app/shared/enums/deadline.enum';
import { customCatchError } from '@app/shared/rxjs/custom-operators';
import { RequestState } from '@app/shared/types/request-state';

@Injectable({
  providedIn: 'root'
})
export class DeadlineContextService {
  private readonly http = inject(MyWorldHttpService);
  private readonly appCtx = inject(AppContextService);

  private readonly deadlines = new BehaviorSubject<
    RequestState<DeadlineDatesModel[]>
  >({
    result: [],
    errors: [],
    status: LoadingStateEnum.idle
  });
  readonly deadlines$ = this.deadlines.asObservable();

  /**
   * Gets a snapshot of the current deadlines state.
   * @returns An object containing the current deadlines state.
   */
  get snapshot() {
    return {
      deadlines: this.deadlines.value
    };
  }

  /**
   * Loads deadlines for the currently selected cycle.
   * Filters deadlines to include only known deadline types and updates the canEdit signal.
   * @returns An Observable that emits the filtered deadlines array.
   */
  loadDeadlines() {
    this.initializeLoadingState();

    return this.http.fetchDeadlines(this.appCtx.snapshot.selectedCycle()).pipe(
      customCatchError(({ message }) => {
        this.deadlines.next({
          errors: [{ detail: message }],
          status: LoadingStateEnum.error,
          result: []
        });
      }),
      map((deadlines) => this.filterValidDeadlines(deadlines.dates)),
      tap((deadlines) => {
        this.deadlines.next({
          errors: [],
          status: LoadingStateEnum.loaded,
          result: deadlines
        });
      })
    );
  }

  /**
   * Initializes the deadlines state with loading status.
   */
  private initializeLoadingState(): void {
    this.deadlines.next({
      errors: [],
      result: [],
      status: LoadingStateEnum.loading
    });
  }

  /**
   * Filters deadlines to include only known deadline types.
   * @param deadlines The raw deadlines array from the API.
   * @returns The filtered deadlines array.
   */
  private filterValidDeadlines(
    deadlines: DeadlineDatesModel[]
  ): DeadlineDatesModel[] {
    const deadlineNames = Object.values(DeadlineNameEnum);
    return deadlines.filter(
      (deadline) =>
        deadlineNames.includes(deadline.name) &&
        deadline.name !== DeadlineNameEnum.SalesStart
    );
  }
}
